import { Wrench } from "lucide-react";
import { useEffect } from "react";
import { Wordmark } from "@/components/ui/wordmark";
import { apiEstaFora } from "../lib/maintenance";

// A tela do modo manutenção (TUT-121 fase 1). Entra no lugar do app inteiro
// quando a queda da API foi confirmada, e SAI SOZINHA: sonda `/api/health` a
// cada 4 segundos e, quando a API responder, recarrega a página — recarregar
// em vez de só limpar o estado é deliberado, porque depois de um deploy os
// bundles mudaram e a árvore antiga rodaria código de outra versão.
//
// A paleta é a da marca (mesma moldura das telas de acesso): fixa, escura nos
// dois temas, porque esta tela é o cartão de visita do produto no momento em
// que ele está fora do ar.

const INTERVALO_DE_SONDAGEM_MS = 4000;

export function MaintenanceScreen({
  onRecovered = () => window.location.reload(),
}: {
  onRecovered?: () => void;
}) {
  useEffect(() => {
    let vivo = true;
    let sondando = false;
    const timer = setInterval(() => {
      if (sondando) return;
      sondando = true;
      void apiEstaFora().then((fora) => {
        sondando = false;
        if (vivo && !fora) onRecovered();
      });
    }, INTERVALO_DE_SONDAGEM_MS);
    return () => {
      vivo = false;
      clearInterval(timer);
    };
  }, [onRecovered]);

  return (
    <main
      className={[
        "relative grid min-h-screen place-items-center overflow-hidden px-6 text-on-dark",
        "bg-[linear-gradient(145deg,var(--brand-night)_0%,color-mix(in_srgb,var(--brand-blue)_70%,var(--brand-night))_62%,var(--brand-blue)_100%)]",
        "before:pointer-events-none before:absolute before:inset-0 before:content-['']",
        "before:opacity-[0.26] before:[background-image:linear-gradient(rgba(91,174,224,0.09)_1px,transparent_1px),linear-gradient(90deg,rgba(91,174,224,0.09)_1px,transparent_1px)]",
        "before:[background-size:36px_36px] before:[mask-image:linear-gradient(to_bottom_right,black,transparent_78%)]",
        "after:pointer-events-none after:absolute after:-right-[180px] after:bottom-[10%] after:h-[420px] after:w-[420px] after:rounded-full after:content-['']",
        "after:[background:radial-gradient(circle,rgba(245,130,32,0.16),transparent_68%)]",
        "[&>*]:relative [&>*]:z-[1]",
      ].join(" ")}
      data-adaptive-flow-surface="maintenance"
    >
      <output
        aria-live="polite"
        data-slot="maintenance-status"
        className="flex max-w-[520px] flex-col items-center gap-[1.1rem] text-center"
      >
        <Wordmark size="large" />
        <span
          aria-hidden="true"
          className="grid h-[3.3rem] w-[3.3rem] place-items-center rounded-xl border border-primary/45 bg-brand-night/50 text-orange [&_svg:not([class*='size-'])]:size-[1.5rem]"
        >
          <Wrench />
        </span>
        <h1 className="m-0 text-titulo-pagina tracking-[-0.045em]">
          Estamos em manutenção
        </h1>
        <p className="m-0 text-subtitulo leading-[1.6] text-on-dark-muted">
          Uma atualização está sendo aplicada neste momento. Seus dados estão
          seguros, e esta tela volta ao sistema sozinha assim que a atualização
          terminar — não é preciso recarregar nem entrar de novo.
        </p>
        <span className="inline-flex items-center gap-[0.55rem] text-corpo text-on-dark-muted/70">
          <span className="h-[0.55rem] w-[0.55rem] animate-pulse rounded-full bg-orange motion-reduce:animate-none" />
          verificando a cada poucos segundos
        </span>
      </output>
    </main>
  );
}
